import { RowNode } from 'ag-grid-community';
import { MultiHierarchyRowGroup, MultiHierarchyRowOverride } from '@opengov/reporting-types';

export const getGroupingValues = (parentNode: RowNode): string[] => {
  let parent = parentNode;
  const groupingValues: string[] = [];
  while (parent.level >= 1) {
    groupingValues.push(parent.key);
    parent = parent.parent;
  }
  groupingValues.reverse();
  return parentNode.level <= 0 ? [] : groupingValues;
};

/**
 * Data fields read off each ag-grid RowNode by getGroupingValuesForMultiHierarchy.
 * Breakdown rows additionally set isBreakdownRow/nodeKey (see rowDataTransforms).
 */
interface MultiHierarchyNodeData {
  groupId?: string;
  isBreakdownRow?: boolean;
  nodeKey?: string;
}

/**
 * Produces the ordered path of groupIds from the expanded breakdown row itself.
 * Breakdown rows encode their preceding non-CoA sibling's groupId in `nodeKey`
 * (format "sibling:self"); including both values gives the backend enough context
 * to filter child queries by the non-CoA parent (e.g. proposal_status='Approved')
 * as well as drill into the CoA group.
 * Returns entries in PATH order (sibling first, then self) so the caller can push
 * them without additional re-ordering.
 *
 * Every other row — including a level-0 root row — falls through to the single-entry `[groupId]`
 * case, which is load-bearing beyond breakdowns: a MERGED root row carries its merge's synthetic id
 * there, and that id is what the rollup pipeline resolves back to the merge's source groups. Do not
 * narrow this to breakdown rows only, or a merged row's drill would fall to the config fallback
 * below and silently request just `grouping[0].groupIds[0]` — one source group of several.
 */
const extractBreakdownRowPathSegments = (data: MultiHierarchyNodeData): string[] => {
  if (!data.isBreakdownRow || !data.nodeKey) {
    return data.groupId ? [data.groupId] : [];
  }
  const [siblingId, selfId] = data.nodeKey.split(':');
  const segments: string[] = [];
  if (siblingId) segments.push(siblingId);
  if (selfId) segments.push(selfId);
  return segments;
};

/**
 * Root group id to seed a top-level child rows request with, so the backend can read
 * `rowAncestorId` from `groupingValues[0]`.
 *
 * Undefined when there is nothing meaningful to send: a non-CoA (columnName) level identifies rows by
 * column value rather than group id, and a zero-level ERP override row has no first level at all —
 * `grouping[0]` is typed as always-present but is undefined at runtime for such a row.
 */
const resolveRootGroupId = (rowgroup: MultiHierarchyRowGroup | MultiHierarchyRowOverride): string | undefined => {
  if (rowgroup.grouping.length === 0) return undefined;
  const firstLevel = rowgroup.grouping[0];
  if (firstLevel.columnName) return undefined;
  return firstLevel.groupIds[0];
};

/**
 * Variant of getGroupingValues for multi-hierarchy reports.
 * Always includes the expanded row's groupId (even at level 0) so the backend
 * can extract rowAncestorId from groupingValues[0] for child row queries.
 * Reads from node.data.groupId (set by rowDataTransforms for multi-hierarchy rows)
 * instead of node.key (which is the display label set by ag-grid).
 * For non-CoA levels (columnName-based), uses the node's groupId directly
 * rather than trying to read from groupIds (which is empty for non-CoA levels).
 * For breakdown rows (visually nested under a non-CoA sibling), also injects the
 * sibling's groupId so the backend can scope the child query to that parent.
 *
 * Accepts an ERP override row as well: it carries the same `grouping` levels and drills
 * identically. An override row may however have NO levels at all (a single flat labelled row),
 * in which case there is no root groupId to send.
 */
export const getGroupingValuesForMultiHierarchy = (
  parentNode: RowNode,
  rowgroup: MultiHierarchyRowGroup | MultiHierarchyRowOverride
): string[] => {
  let node: RowNode = parentNode;
  const groupingValues: string[] = [];
  while (node.level >= 0) {
    const data = (node.data ?? {}) as MultiHierarchyNodeData;
    const segments = extractBreakdownRowPathSegments(data);
    if (segments.length > 0) {
      // node is a breakdown row: push in reverse because the outer loop reverses at the end.
      for (let i = segments.length - 1; i >= 0; i--) {
        groupingValues.push(segments[i]);
      }
    } else if (node.level === 0) {
      const rootGroupId = resolveRootGroupId(rowgroup);
      if (rootGroupId !== undefined) {
        groupingValues.push(rootGroupId);
      }
    }
    node = node.parent;
  }
  groupingValues.reverse();
  return groupingValues;
};

/**
 * Config index of the breakdown level a row sits on, counted as the number of breakdown rows on
 * its path back to the root (a breakdown row is where the tree crosses into the next level).
 *
 * The backend needs this because `groupingValues` cannot express it: the path gains one entry both
 * when drilling deeper inside a single level's group hierarchy and when crossing into the next
 * breakdown level, so a Fund→Object breakdown drill and a two-deep Fund drill look identical.
 */
export const getBreakdownLevelForMultiHierarchy = (parentNode: RowNode): number => {
  let node: RowNode = parentNode;
  let breakdownLevel = 0;
  while (node.level >= 0) {
    if ((node.data as MultiHierarchyNodeData | undefined)?.isBreakdownRow) {
      breakdownLevel++;
    }
    node = node.parent;
  }
  return breakdownLevel;
};

/**
 * Absolute config breakdown level sent as `rowBreakdownLevel` on child drills (BUD-18770).
 *
 * Injected root-level breakdown rows all sit at ag-grid level 0; their server-supplied absolute
 * level is authoritative. In-tree rows use the ancestor walk.
 */
export function resolveDrillBreakdownLevel(node: RowNode): number {
  const data = node.data as { isRootLevelBreakdown?: boolean; breakdownLevel?: number } | undefined;
  if (data?.isRootLevelBreakdown && typeof data.breakdownLevel === 'number') {
    return data.breakdownLevel;
  }
  return getBreakdownLevelForMultiHierarchy(node);
}

/**
 * Produces a stable row ID for multi-hierarchy parent rows so that rows from
 * different groupIds within the same rowGroup remain distinct in ag-grid.
 */
export function buildMultiHierarchyParentRowId(rowGroupId: string, groupId: string): string {
  return `${rowGroupId}:${groupId}`;
}
